import { createLogger } from '@/lib/logger'
import type { Payload } from 'payload'

const logger = createLogger('EmailDomainBlocklist')

// Shown to users who try to sign up / subscribe with a blocked email domain.
// Intentionally generic — it never reveals which domains are blocked.
export const WORK_EMAIL_ERROR_MESSAGE = 'Please use a business email address'

// Marker baked into the error message so the magic-link flow can still
// recognize a blocked-domain rejection after Auth.js has wrapped it.
const BLOCKED_EMAIL_DOMAIN_MARKER = 'BLOCKED_EMAIL_DOMAIN'

// Thrown from user-creation paths (the Payload Auth.js adapter) when an email
// domain is on the blocklist. Carries a marker + flag so callers can detect it
// even after Auth.js wraps it in its own error type.
export class BlockedEmailDomainError extends Error {
  readonly isBlockedEmailDomain = true

  constructor(domain?: string) {
    super(`${BLOCKED_EMAIL_DOMAIN_MARKER}: ${domain ?? 'unknown'}`)
    this.name = 'BlockedEmailDomainError'
  }
}

// Normalize an arbitrary domain/email-domain string to a bare, comparable host:
// lowercase, trimmed, with protocol/mailto/userinfo/path/port stripped. So
// "QQ.com", "@qq.com", "user@qq.com" and "https://qq.com/x" all collapse to
// "qq.com". Returns '' when nothing usable remains.
export function normalizeDomain(input: string): string {
  if (!input) return ''
  let value = input.trim().toLowerCase()
  if (!value) return ''

  value = value.replace(/^mailto:/, '').replace(/^[a-z][a-z0-9+.-]*:\/\//, '')

  // If userinfo / a full address was passed, keep only what follows the last "@"
  const atIndex = value.lastIndexOf('@')
  if (atIndex !== -1) value = value.slice(atIndex + 1)

  // Drop any path, query string, or port
  value = value.split('/')[0]?.split('?')[0]?.split(':')[0] ?? ''

  return value.trim()
}

// Extract the (normalized) domain portion of an email address. Returns null if
// the value has no plausible domain (no "." after normalization).
export function getEmailDomain(email: string): string | null {
  const domain = normalizeDomain(email)
  return domain.includes('.') ? domain : null
}

// Core lookup: true if the email's domain is on the email_domain_blacklist.
// Lets a lookup failure propagate so callers decide whether to fail open or closed.
async function lookupEmailDomainBlocked(payload: Payload, email: string): Promise<boolean> {
  const domain = getEmailDomain(email)
  if (!domain) return false

  const { docs } = await payload.find({
    collection: 'email_domain_blacklist',
    where: { domain: { equals: domain } },
    limit: 1,
    depth: 0,
    pagination: false,
  })
  return docs.length > 0
}

// Fails OPEN (returns false) on any lookup error so a blocklist outage can
// never wedge signups/subscriptions entirely. Use at soft entry points
// (newsletter, deal claims, the magic-link request action).
export async function isEmailDomainBlocked(payload: Payload, email: string): Promise<boolean> {
  try {
    return await lookupEmailDomainBlocked(payload, email)
  } catch (error) {
    logger.error('Failed to check email domain blocklist:', error)
    return false
  }
}

// Fails CLOSED: a lookup failure propagates instead of being swallowed, so hard
// write boundaries (the Users beforeChange hook) reject the write rather than let
// a blocked domain slip through on a transient blacklist outage.
export async function isEmailDomainBlockedStrict(
  payload: Payload,
  email: string,
): Promise<boolean> {
  return lookupEmailDomainBlocked(payload, email)
}

// Detect a blocked-domain rejection inside an error that Auth.js may have
// wrapped before it reaches us. Auth.js nests the original under varying shapes
// (`.cause`, `.cause.err`, AggregateError `.errors`), so search breadth-first
// across all of them rather than following a single chain.
export function isBlockedEmailDomainError(error: unknown): boolean {
  const queue: unknown[] = [error]
  const seen = new Set<unknown>()

  while (queue.length > 0) {
    const current = queue.shift()
    if (current == null || seen.has(current)) continue
    seen.add(current)
    if (seen.size > 24) break

    if (current instanceof BlockedEmailDomainError) return true
    if (
      typeof current === 'object' &&
      (current as { isBlockedEmailDomain?: boolean }).isBlockedEmailDomain === true
    ) {
      return true
    }
    if (current instanceof Error && current.message.includes(BLOCKED_EMAIL_DOMAIN_MARKER)) {
      return true
    }

    if (typeof current === 'object') {
      const node = current as { cause?: unknown; err?: unknown; errors?: unknown }
      queue.push(node.cause, node.err)
      if (Array.isArray(node.errors)) queue.push(...node.errors)
    }
  }

  return false
}
